const BOB_MODEL_URL = '/assets/bob-bounce/01-bob.glb';
const CLIPS = ['Idle', 'Run', 'Jump', 'Land', 'Cheer', 'Hit', 'Dance'];
const LOOPING = new Set(['Idle', 'Run']);
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/** Fetch and parse one fixed Bob GLB with CSP-compatible embedded images.
 * The caller supplies cancellation and owns partial textures through own.
 * Reject unavailable files or decode failures; no global asset cache is used. */
export async function loadBobAsset({ THREE, url, signal, own }) {
  const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
  signal.throwIfAborted();
  const loader = new GLTFLoader();
  // Match the existing Sky loader: embedded textures use img-src blob rather
  // than ImageBitmapLoader's fetch, which the site's connect-src rejects.
  loader.register((parser) => ({
    name: 'MYAUDIENCE_bob_embedded_images',
    loadTexture(index) {
      const source = parser.json.textures[index].source;
      if (source === undefined || parser.json.images[source].bufferView === undefined) return null;
      const imageLoader = new THREE.TextureLoader(parser.options.manager);
      return parser
        .loadTextureImage(index, source, {
          load(url, done, progress, failed) {
            return imageLoader.load(url, done, progress, (error) => {
              URL.revokeObjectURL(url);
              failed(error);
            });
          },
        })
        .then((texture) => {
          if (!texture) throw new Error('Bob artwork texture could not decode.');
          own(texture);
          return texture;
        });
    },
  }));
  const response = await fetch(url, {
    signal: AbortSignal.any([signal, AbortSignal.timeout(20_000)]),
  });
  if (!response.ok) throw new Error('Bob artwork unavailable.');
  const bytes = await response.arrayBuffer();
  signal.throwIfAborted();
  return loader.parseAsync(bytes, '');
}

/** Own imported geometry, materials, textures and skeletons once per asset.
 * Collecting a late scene after disposal immediately releases its resources;
 * imported ImageBitmaps are closed once, including shared texture sources. */
export function createBobAssetResources() {
  const owned = new Set(),
    released = new WeakSet(),
    closedImages = new WeakSet();
  let closed = false;
  function release(resource) {
    if (!resource || released.has(resource)) return;
    released.add(resource);
    resource.dispose?.();
    const image = resource.isTexture ? resource.source?.data : null;
    if (image?.close && !closedImages.has(image)) {
      closedImages.add(image);
      image.close();
    }
  }
  function own(resource) {
    if (!resource) return;
    if (closed) release(resource);
    else owned.add(resource);
  }
  function collect(scene) {
    scene?.traverse((node) => {
      if (!node.isMesh) return;
      own(node.geometry);
      if (node.isSkinnedMesh) own(node.skeleton);
      for (const material of Array.isArray(node.material) ? node.material : [node.material]) {
        own(material);
        for (const value of Object.values(material || {})) if (value?.isTexture) own(value);
      }
    });
  }
  return {
    own,
    collect,
    dispose() {
      closed = true;
      for (const resource of owned) release(resource);
      owned.clear();
    },
  };
}

/** Load one original, normalized skeletal Bob per renderer world. The GLB owns
 * its 1.9-unit height, feet at -0.85 and local -Z facing; the caller owns world
 * placement and scale. dt is visible seconds from the existing rendering clock.
 * Failure keeps the procedural body. No score, physics, timer or global cache.
 * An injected loader returns {scene, animations}; own captures partial resources
 * during asynchronous parsing so stale completions release GPU/image data too.
 * Optional update argument {phase, seconds} samples a finite scripted performance
 * without changing the canonical state. ready resolves after accepted rig setup. */
export function createBobArt({
  THREE,
  bob,
  prepareMaterial = (material) => material,
  onInvalidate = () => {},
  onStatus = () => {},
  loadModel = loadBobAsset,
  url = BOB_MODEL_URL,
}) {
  const abort = new AbortController();
  const resources = createBobAssetResources();
  const initialVisibility = bob.body.visible;
  let disposed = false,
    model = null,
    mixer = null;
  let actions = {},
    current = null,
    outgoing = null,
    blend = 0;
  let lastState = null,
    lastReduced = false,
    lastPerformance = null,
    performancePhase = null,
    previousElapsed = -1,
    serial = 0;
  let reaction = null,
    reactionAge = 0,
    priorStatus = null;
  let status = 'loading';
  function report(value) {
    status = value;
    onStatus(value);
  }

  function closeResources() {
    mixer?.stopAllAction();
    if (model) mixer?.uncacheRoot(model);
    model?.removeFromParent();
    resources.dispose();
    model = mixer = null;
    actions = {};
  }
  function reset() {
    mixer.stopAllAction();
    mixer.setTime(0);
    current = outgoing = reaction = null;
    blend = reactionAge = serial = 0;
    priorStatus = null;
  }
  function performanceName(phase) {
    return phase === 'intro'
      ? 'Dance'
      : ['ready', 'preparing', 'countdown'].includes(phase)
        ? 'Idle'
        : ['reward', 'results'].includes(phase)
          ? 'Cheer'
          : null;
  }
  function present(performance, reduced, state) {
    if (performance?.phase === 'results' && state.status !== 'complete') return false;
    const name = performanceName(performance?.phase);
    if (!name) return false;
    if (performancePhase !== performance.phase) {
      reset();
      performancePhase = performance.phase;
    }
    select(name, true);
    const action = actions[name];
    // The coordinator supplies finite presentation age. Sampling a provider
    // clip never advances simulation, consumes a reward or creates a timer.
    action.time =
      performance.phase === 'results'
        ? action.getClip().duration
        : reduced
          ? action.getClip().duration * (name === 'Cheer' ? 0.55 : 0.35)
          : clamp(Number(performance.seconds) || 0, 0, action.getClip().duration);
    action.setEffectiveWeight(1);
    mixer.update(0);
    model.updateMatrixWorld(true);
    return true;
  }
  function select(name, reduced, restart = false) {
    if (name === current && !restart) return;
    if (outgoing) actions[outgoing].stop();
    outgoing = name !== current ? current : null;
    current = name;
    const action = actions[name];
    action.reset().setEffectiveTimeScale(1).setEffectiveWeight(1);
    action.setLoop(LOOPING.has(name) ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    action.clampWhenFinished = true;
    action.play();
    blend = 0;
    if (reduced && outgoing) {
      actions[outgoing].stop();
      outgoing = null;
    }
  }
  function pose(state, dt, reduced, performance = null) {
    if (present(performance, reduced, state)) return;
    if (performancePhase) {
      reset();
      performancePhase = null;
    }
    if (state.elapsed < previousElapsed || state.eventSerial < serial) reset();
    previousElapsed = state.elapsed;
    reactionAge += dt;
    let restart = false;
    for (const event of state.events || []) {
      if (event.id <= serial) continue;
      serial = event.id;
      if (event.type === 'stumble' || event.type === 'fail') {
        reaction = 'Hit';
        reactionAge = 0;
        restart = true;
      } else if (['land', 'platform', 'ground'].includes(event.type) && reaction !== 'Hit') {
        reaction = 'Land';
        reactionAge = 0;
      } else if (event.type === 'spring' && reaction !== 'Hit') {
        reaction = null;
        restart = current === 'Jump';
      }
    }
    if (reaction && reactionAge >= Math.min(actions[reaction].getClip().duration, 0.65))
      reaction = null;
    const b = state.bob;
    // The landing clip starts the crouch as Bob descends into the bounce zone.
    // This pose is advisory; only domain events represent an actual contact.
    const landing = b.vy < -0.5 && b.y < 1.4;
    const airborne = Math.abs(b.vy) > 0.5 || b.y > 1.1;
    const name =
      state.status === 'complete'
        ? 'Cheer'
        : state.status === 'failed'
          ? 'Hit'
          : state.status === 'ready'
            ? 'Idle'
            : reaction ||
              (landing ? 'Land' : airborne ? 'Jump' : Math.abs(b.vz) > 0.5 ? 'Run' : 'Idle');
    if (state.status === 'complete' || state.status === 'failed')
      restart = priorStatus !== state.status;
    select(name, reduced, restart && ['Hit', 'Jump'].includes(name));
    const action = actions[current];
    if (reduced) {
      if (outgoing) actions[outgoing].stop();
      outgoing = null;
      action.time = action.getClip().duration * (current === 'Cheer' ? 0.55 : 0.35);
      action.setEffectiveWeight(1);
      mixer.update(0);
    } else {
      blend = Math.min(1, blend + dt / 0.2);
      action.setEffectiveWeight(outgoing ? blend : 1);
      if (outgoing) {
        actions[outgoing].setEffectiveWeight(1 - blend);
        if (blend === 1) {
          actions[outgoing].stop();
          outgoing = null;
        }
      }
      // One-shot clips clamp on their last authored pose. Holding a result
      // never restarts its performance or creates an independent render loop.
      mixer.update(dt);
    }
    priorStatus = state.status;
    model.updateMatrixWorld(true);
  }
  function update(state, dt = 0, reduced = false, performance = null) {
    if (disposed) return;
    lastState = state;
    lastReduced = reduced;
    lastPerformance = performance;
    if (!mixer) return;
    pose(state, clamp(Number(dt) || 0, 0, 0.05), reduced, performance);
  }

  report('loading');
  const ready = Promise.resolve()
    .then(() =>
      loadModel({
        THREE,
        url,
        signal: abort.signal,
        own: resources.own,
      }),
    )
    .then((asset) => {
      resources.collect(asset?.scene);
      if (disposed) return false;
      let skinned = false;
      asset?.scene?.traverse((node) => {
        if (node.isSkinnedMesh) skinned = true;
      });
      const findClip = (name) =>
        asset?.animations?.find(
          (clip) =>
            clip.name === name &&
            Number.isFinite(clip.duration) &&
            clip.duration > 0 &&
            clip.tracks.length,
        );
      const clips = CLIPS.map(findClip);
      if (!skinned || clips.some((clip) => !clip)) throw new Error('Bob rig or clips incomplete.');
      model = asset.scene;
      model.name = 'Bob — original Tripo animated character';
      model.traverse((node) => {
        if (!node.isMesh) return;
        node.castShadow = node.receiveShadow = true;
        node.frustumCulled = !node.isSkinnedMesh;
        for (const material of Array.isArray(node.material) ? node.material : [node.material])
          prepareMaterial(material);
      });
      mixer = new THREE.AnimationMixer(model);
      actions = Object.fromEntries(clips.map((clip) => [clip.name, mixer.clipAction(clip)]));
      bob.root.add(model);
      if (lastState) pose(lastState, 0, lastReduced, lastPerformance);
      else {
        select('Idle', false);
        mixer.update(0);
      }
      bob.body.visible = false;
      report('ready');
      onInvalidate();
      return true;
    })
    .catch(() => {
      closeResources();
      if (!disposed) {
        bob.body.visible = initialVisibility;
        report('fallback');
        onInvalidate();
      }
      return false;
    });
  return {
    ready,
    update,
    get status() {
      return status;
    },
    presentationDuration(phase) {
      return actions[performanceName(phase)]?.getClip().duration || 0;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      status = 'disposed';
      abort.abort();
      closeResources();
      bob.body.visible = initialVisibility;
    },
  };
}
